import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Container,
    Form,
    InputGroup,
    Spinner,
} from "react-bootstrap";
import { getAuthToken } from "../../utils/auth";

// Settings not listed in any group are shown under "Other", so new backend
// settings always appear here.
const SETTING_GROUPS = [
    {
        id: "premium",
        title: "Premium & Subscriptions",
        description: "Who gets premium content on each platform and whether subscription plans are shown.",
        keys: ["ios_premium_mode", "android_premium_mode", "show_subscription_plans"],
    },
    {
        id: "login",
        title: "Login & SMS Verification",
        description: "Phone number that receives users' SMS tokens, and testing shortcuts.",
        keys: ["sms_receiver_phone", "enable_testing_mode"],
    },
    {
        id: "vip",
        title: "App Store Review (VIP login)",
        description: "Demo numbers that log in with a password instead of SMS. Never add real users' numbers.",
        keys: ["vip_login_enabled", "vip_phone_numbers", "vip_password"],
    },
    {
        id: "contact",
        title: "Contact Info",
        description: "Shown in the app and on the website.",
        keys: ["contact_email", "contact_phone"],
    },
];

const SETTING_LABELS = {
    ios_premium_mode: "iOS premium mode",
    android_premium_mode: "Android premium mode",
    show_subscription_plans: "Show subscription plans",
    sms_receiver_phone: "SMS receiver phone",
    enable_testing_mode: "Testing mode",
    vip_login_enabled: "VIP login enabled",
    vip_phone_numbers: "VIP phone numbers",
    vip_password: "VIP password",
    contact_email: "Contact email",
    contact_phone: "Contact phone",
};

const SELECT_OPTIONS = {
    ios_premium_mode: ["FREE", "VIP_ONLY", "PAID"],
    android_premium_mode: ["FREE", "VIP_ONLY", "PAID"],
};

const SECRET_KEYS = new Set(["vip_password"]);

const humanize = (key) =>
    key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const isBooleanSetting = (setting) =>
    setting.type === "BOOLEAN" || setting.value === "true" || setting.value === "false";

const AppSettingsManagement = () => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const token = getAuthToken();

    const [settings, setSettings] = useState([]);
    const [savedValues, setSavedValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);
    const [visibleSecrets, setVisibleSecrets] = useState({});
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    // =========================
    // FETCH SETTINGS
    // =========================
    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);

            const url = new URL(`${baseUrl}/admin/settings`);

            const res = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!res.ok) {
                console.error("Failed to fetch settings:", res.statusText);
                setErrorMessage("Failed to load settings.");
                return;
            }

            const result = await res.json();
            const data = result.status ? result.data || [] : [];

            setSettings(data);
            setSavedValues(Object.fromEntries(data.map((s) => [s.key, s.value])));
        } catch (err) {
            console.error("Error fetching settings:", err);
            setErrorMessage("Failed to load settings.");
        } finally {
            setLoading(false);
        }
    }, [baseUrl, token]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // =========================
    // SAVE SETTING
    // =========================
    const saveSetting = async (setting, value) => {
        const { key } = setting;
        setSavingKey(key);
        setErrorMessage("");

        try {
            const url = new URL(`${baseUrl}/admin/settings/${key}`);
            const body = isBooleanSetting(setting)
                ? { value, enabled: value }
                : { value };

            const res = await fetch(url, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
            });

            const result = res.ok ? await res.json() : null;

            if (!result?.status) {
                console.error("Failed update:", res.statusText);
                setErrorMessage(`Failed to update "${SETTING_LABELS[key] || key}".`);
                setValue(key, savedValues[key]);
                return;
            }

            setValue(key, value);
            setSavedValues((prev) => ({ ...prev, [key]: value }));
            setSuccessMessage(`"${SETTING_LABELS[key] || key}" updated`);
            setTimeout(() => setSuccessMessage(""), 1500);
        } catch (err) {
            console.error(err);
            setErrorMessage(`Failed to update "${SETTING_LABELS[key] || key}".`);
            setValue(key, savedValues[key]);
        } finally {
            setSavingKey(null);
        }
    };

    const setValue = (key, value) => {
        setSettings((prev) =>
            prev.map((s) => (s.key === key ? { ...s, value } : s))
        );
    };

    const saveIfChanged = (setting, value) => {
        if (value !== savedValues[setting.key]) {
            saveSetting(setting, value);
        }
    };

    // =========================
    // GROUPING
    // =========================
    const groups = useMemo(() => {
        const byKey = Object.fromEntries(settings.map((s) => [s.key, s]));
        const groupedKeys = new Set(SETTING_GROUPS.flatMap((g) => g.keys));

        const result = SETTING_GROUPS.map((group) => ({
            ...group,
            settings: group.keys.map((key) => byKey[key]).filter(Boolean),
        }));

        const other = settings.filter((s) => !groupedKeys.has(s.key));
        if (other.length > 0) {
            result.push({
                id: "other",
                title: "Other",
                description: "Settings that are not grouped yet.",
                settings: other,
            });
        }

        return result.filter((group) => group.settings.length > 0);
    }, [settings]);

    // =========================
    // CONTROLS
    // =========================
    const renderControl = (setting) => {
        const { key } = setting;
        const disabled = savingKey === key;

        if (isBooleanSetting(setting)) {
            return (
                <Form.Check
                    type="switch"
                    id={`setting-${key}`}
                    checked={setting.value === "true"}
                    disabled={disabled}
                    onChange={(e) => saveSetting(setting, e.target.checked.toString())}
                    style={{ transform: "scale(1.3)" }}
                />
            );
        }

        if (SELECT_OPTIONS[key]) {
            return (
                <Form.Select
                    value={setting.value || ""}
                    disabled={disabled}
                    onChange={(e) => saveSetting(setting, e.target.value)}
                    style={{ width: 140 }}
                >
                    {!SELECT_OPTIONS[key].includes(setting.value) && (
                        <option value={setting.value || ""}>
                            {setting.value || "—"}
                        </option>
                    )}
                    {SELECT_OPTIONS[key].map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </Form.Select>
            );
        }

        const isSecret = SECRET_KEYS.has(key);
        const input = (
            <Form.Control
                type={isSecret && !visibleSecrets[key] ? "password" : "text"}
                value={setting.value || ""}
                disabled={disabled}
                autoComplete="off"
                onChange={(e) => setValue(key, e.target.value)}
                onBlur={(e) => saveIfChanged(setting, e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            />
        );

        return (
            <InputGroup style={{ width: 280 }}>
                {input}
                {isSecret && (
                    <Button
                        variant="outline-secondary"
                        onClick={() =>
                            setVisibleSecrets((prev) => ({ ...prev, [key]: !prev[key] }))
                        }
                    >
                        {visibleSecrets[key] ? "Hide" : "Show"}
                    </Button>
                )}
            </InputGroup>
        );
    };

    if (loading) {
        return (
            <div className="text-center py-4">
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <Container className="mt-4" style={{ maxWidth: 860 }}>
            <h3 className="mb-3">App Settings</h3>

            {successMessage && (
                <Alert variant="success" className="py-2">
                    {successMessage}
                </Alert>
            )}
            {errorMessage && (
                <Alert
                    variant="danger"
                    className="py-2"
                    dismissible
                    onClose={() => setErrorMessage("")}
                >
                    {errorMessage}
                </Alert>
            )}

            {groups.map((group) => (
                <Card key={group.id} className="mb-4 shadow-sm">
                    <Card.Header className="bg-body">
                        <div className="fw-bold">{group.title}</div>
                        <div className="text-muted small">{group.description}</div>
                    </Card.Header>

                    <Card.Body className="p-0">
                        {group.settings.map((setting, index) => (
                            <div
                                key={setting.key}
                                className={`d-flex align-items-center justify-content-between gap-3 px-3 py-3 ${
                                    index > 0 ? "border-top" : ""
                                }`}
                            >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="fw-semibold">
                                        {SETTING_LABELS[setting.key] || humanize(setting.key)}
                                    </div>
                                    <code className="small text-muted">{setting.key}</code>
                                    {setting.description && (
                                        <div className="text-muted small mt-1">
                                            {setting.description}
                                        </div>
                                    )}
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                    {savingKey === setting.key && (
                                        <Spinner animation="border" size="sm" />
                                    )}
                                    {renderControl(setting)}
                                </div>
                            </div>
                        ))}
                    </Card.Body>
                </Card>
            ))}
        </Container>
    );
};

export default AppSettingsManagement;
